import { type AudioTheme, SCALE_INTERVALS, scaleDegreeToMidi, waveGain } from './audioThemes.js';
import type { GearSoundEvent } from './gearSound.js';
import { midiToFreq } from './backgroundMusic.js';

/**
 * Звуки хода / отката / соединения пары / победы в теме стиля. Высота звука хода
 * идёт по ладу темы и растёт с длиной пути, поэтому звуки всегда попадают в тон
 * музыке того же уровня.
 */

const SFX_OCTAVES_UP = 2; // ступени лада играем на две октавы выше тоники

function noiseBurst(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  dur: number,
  centerHz: number,
  gain: number,
): void {
  const size = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
  const data = buffer.getChannelData(0)!;
  for (let i = 0; i < size; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / size);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = centerHz;
  bp.Q.value = 5;
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  src.connect(bp);
  bp.connect(g);
  g.connect(dest);
  src.start(t0);
  src.stop(t0 + dur + 0.01);
}

function tone(
  ctx: AudioContext,
  theme: AudioTheme,
  t0: number,
  freq: number,
  dur: number,
  gain: number,
  glideOct = 0,
): void {
  const osc = ctx.createOscillator();
  osc.type = theme.sfx.wave;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideOct !== 0) {
    osc.frequency.exponentialRampToValueAtTime(freq * 2 ** glideOct, t0 + dur);
  }
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(gain * waveGain(theme.sfx.wave) * theme.sfxGain, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  // square/saw режем фильтром, чтобы не резали слух
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = Math.min(6000, freq * 6);
  osc.connect(lp);
  lp.connect(g);
  g.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Нота звука хода для длины пути (идёт по ладу, 2 октавы вверх и по кругу). */
export function sfxStepMidi(theme: AudioTheme, pathLength: number): number {
  const len = SCALE_INTERVALS[theme.scale].length;
  const degree = Math.min(Math.max(pathLength, 0), len * 2) - 1;
  return scaleDegreeToMidi(theme, degree) + SFX_OCTAVES_UP * 12;
}

export function playThemedSfx(ctx: AudioContext, theme: AudioTheme, event: GearSoundEvent): void {
  const t0 = ctx.currentTime;
  const { sfx } = theme;
  const len = SCALE_INTERVALS[theme.scale].length;

  switch (event.kind) {
    case 'extend': {
      const freq = midiToFreq(sfxStepMidi(theme, event.pathLength ?? 2));
      tone(ctx, theme, t0, freq, sfx.decay, 0.26 * (1 - sfx.noise * 0.4), sfx.glide);
      if (sfx.noise > 0) {
        noiseBurst(ctx, ctx.destination, t0, 0.03 + sfx.decay * 0.25, freq * 1.5, sfx.noise * 0.35 * theme.sfxGain);
      }
      break;
    }
    case 'backtrack': {
      const freq = midiToFreq(sfxStepMidi(theme, (event.pathLength ?? 2) - 1));
      tone(ctx, theme, t0, freq * 0.8, sfx.decay * 0.7, 0.18, -Math.abs(sfx.glide) - 0.15);
      break;
    }
    case 'pairComplete': {
      // быстрый аккорд-арпеджио тоники вверх: тоника-терция-квинта-октава
      [0, 2, 4, len].forEach((k, i) => {
        const freq = midiToFreq(scaleDegreeToMidi(theme, k) + SFX_OCTAVES_UP * 12);
        tone(ctx, theme, t0 + i * 0.065, freq, 0.22 + sfx.decay * 0.5, 0.15);
      });
      break;
    }
    case 'win': {
      // восходящая мелодия по ладу, затем длинный аккорд
      [0, 2, 4, len, len + 2, len + 4].forEach((k, i) => {
        const freq = midiToFreq(scaleDegreeToMidi(theme, k) + SFX_OCTAVES_UP * 12);
        tone(ctx, theme, t0 + i * 0.11, freq, 0.35, 0.16);
      });
      const tEnd = t0 + 6 * 0.11;
      [0, 2, 4].forEach((k) => {
        const freq = midiToFreq(scaleDegreeToMidi(theme, k + len) + SFX_OCTAVES_UP * 12);
        tone(ctx, theme, tEnd, freq, 1.1, 0.11);
      });
      break;
    }
  }
}
